<template>
  <!-- Hero de ancho completo -->
  <section class="relative mt-8 overflow-hidden shadow-lg">
    <div class="max-w-7xl mx-auto">
      <!-- Imagen de fondo con blur -->
      <div class="absolute inset-0">
        <img 
          src="/images/Image_fx xecuencia.jpg" 
          alt="Xecuenxia - Análisis de Laboratorios con IA" 
          class="w-full h-full object-cover filter blur-sm"
        />
        <!-- Overlay para mejorar legibilidad -->
        <div class="absolute inset-0 bg-black bg-opacity-70"></div>
      </div>
      
      <!-- Contenido del hero -->
      <div class="relative px-6 py-10 md:py-14 grid md:grid-cols-2 gap-8 items-center">
        <div>
          <h1 class="text-3xl md:text-4xl font-bold text-white leading-tight tracking-wide">
            Análisis Clínico <span class="text-blue-300">de Precisión</span> Respaldado por IA
          </h1>
          <p class="mt-6 text-white text-lg leading-relaxed font-light">
            Plataforma especializada en interpretación diagnóstica avanzada de examenes de laboratorio.
          </p>
          <ul class="mt-4 text-white text-base space-y-3 list-none font-light">
            <li class="flex items-start"><span class="mr-3 text-blue-300 font-bold">•</span> Análisis exhaustivo de parámetros bioquímicos y hematológicos</li>
            <li class="flex items-start"><span class="mr-3 text-blue-300 font-bold">•</span> Correlación clínica con hallazgos significativos</li>
            <li class="flex items-start"><span class="mr-3 text-blue-300 font-bold">•</span> Recomendaciones basadas en evidencia científica</li>
          </ul>
          <div class="mt-6 flex flex-col sm:flex-row gap-4">
            <template v-if="!authStore.isAuthenticated">
              <button @click="openAuthModal('login')" class="px-10 py-4 bg-transparent text-white rounded-md hover:bg-blue-800 transition-all text-center text-base font-semibold border-2 border-white uppercase tracking-wider">
                Ingresar al Sistema
              </button>
            </template>
            <template v-else>
              <router-link to="/reports" class="px-10 py-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-all text-center text-base font-semibold uppercase tracking-wider">
                Ir a Mis Reportes
              </router-link>
            </template>
          </div>
        </div>
        <div class="hidden md:block">
          <div class="flex justify-center">
            <img 
              src="/images/Image_fx xecuencia.jpg" 
              alt="SABIOLAB - Análisis Inteligente de Laboratorios" 
              class="max-w-full h-auto rounded-lg shadow-lg"
            />
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Contenido principal con ancho limitado -->
  <div class="max-w-7xl mx-auto">
    <!-- Proceso de Análisis -->
    <section class="mt-16">
      <div class="text-center mb-12">
        <h2 class="text-3xl font-bold text-gray-900 mb-4">Proceso de Análisis Clínico</h2>
        <div class="w-24 h-1 bg-blue-800 mx-auto rounded-full"></div>
      </div>
      
      <div class="grid md:grid-cols-3 gap-8">
        <!-- PASO 1 -->
        <div class="group relative overflow-hidden rounded-lg shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
          <div class="relative h-80 bg-gradient-to-br from-blue-50 to-blue-100">
            <img 
              src="/images/1.png" 
              alt="Carga de Documentos" 
              class="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-500"
            >
            <div class="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
            
            <!-- Contenido superpuesto -->
            <div class="absolute bottom-0 left-0 right-0 p-6 text-white">
              <div class="flex items-center justify-between mb-3">
                <span class="bg-blue-800 text-white px-3 py-1 rounded-full text-sm font-bold">PASO 1</span>
                <div class="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd"></path>
                  </svg>
                </div>
              </div>
              <h3 class="text-xl font-bold mb-2">Carga Segura de Documentos</h3>
              <p class="text-sm text-gray-200 leading-relaxed">Ingreso de resultados de laboratorio mediante protocolo encriptado. Formatos aceptados: PDF, DOCX.</p>
            </div>
          </div>
        </div>

        <!-- PASO 2 -->
        <div class="group relative overflow-hidden rounded-lg shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
          <div class="relative h-80 bg-gradient-to-br from-purple-50 to-purple-100">
            <img 
              src="/images/2.png" 
              alt="Procesamiento IA Médica" 
              class="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-500"
            >
            <div class="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
            
            <!-- Contenido superpuesto -->
            <div class="absolute bottom-0 left-0 right-0 p-6 text-white">
              <div class="flex items-center justify-between mb-3">
                <span class="bg-purple-800 text-white px-3 py-1 rounded-full text-sm font-bold">PASO 2</span>
                <div class="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clip-rule="evenodd"></path>
                  </svg>
                </div>
              </div>
              <h3 class="text-xl font-bold mb-2">Procesamiento por IA Médica</h3>
              <p class="text-sm text-gray-200 leading-relaxed">Sistema multi-agente de validación, interpretación clínica y auditoría de calidad diagnóstica.</p>
            </div>
          </div>
        </div>

        <!-- PASO 3 -->
        <div class="group relative overflow-hidden rounded-lg shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
          <div class="relative h-80 bg-gradient-to-br from-green-50 to-green-100">
            <img 
              src="/images/3.jpg" 
              alt="Reporte Diagnóstico" 
              class="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-500"
            >
            <div class="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
            
            <!-- Contenido superpuesto -->
            <div class="absolute bottom-0 left-0 right-0 p-6 text-white">
              <div class="flex items-center justify-between mb-3">
                <span class="bg-green-800 text-white px-3 py-1 rounded-full text-sm font-bold">PASO 3</span>
                <div class="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V4a2 2 0 00-2-2H6zm1 2a1 1 0 000 2h6a1 1 0 100-2H7zm6 7a1 1 0 011 1v3a1 1 0 11-2 0v-3a1 1 0 011-1zm-3 3a1 1 0 100 2h.01a1 1 0 100-2H10zm-4 1a1 1 0 011-1h.01a1 1 0 110 2H7a1 1 0 01-1-1zm1-4a1 1 0 100 2h.01a1 1 0 100-2H7zm2 0a1 1 0 100 2h.01a1 1 0 100-2H9zm2 0a1 1 0 100 2h.01a1 1 0 100-2h-.01z" clip-rule="evenodd"></path>
                  </svg>
                </div>
              </div>
              <h3 class="text-xl font-bold mb-2">Reporte Diagnóstico Integral</h3>
              <p class="text-sm text-gray-200 leading-relaxed">Informe estructurado con hallazgos clínicos, análisis de riesgo y recomendaciones terapéuticas.</p>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Línea conectora -->
      <div class="hidden md:flex items-center justify-center mt-8 mb-8">
        <div class="flex items-center space-x-4">
          <div class="w-3 h-3 bg-blue-800 rounded-full"></div>
          <div class="w-16 h-0.5 bg-gradient-to-r from-blue-800 to-purple-800"></div>
          <div class="w-3 h-3 bg-purple-800 rounded-full"></div>
          <div class="w-16 h-0.5 bg-gradient-to-r from-purple-800 to-green-800"></div>
          <div class="w-3 h-3 bg-green-800 rounded-full"></div>
        </div>
      </div>
    </section>

    <!-- Seguridad y Confiabilidad -->
    <section class="mt-20">
      <div class="text-center mb-12">
        <h3 class="text-3xl font-bold text-gray-900 mb-4">Seguridad y Confiabilidad</h3>
        <div class="w-24 h-1 bg-blue-800 mx-auto rounded-full"></div>
      </div>
      
      <div class="grid md:grid-cols-2 gap-8">
        <!-- Protección de Datos -->
        <div class="group relative overflow-hidden rounded-xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1">
          <div class="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 p-8 min-h-80">
            <div class="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-transparent"></div>
            
            <!-- Icono de seguridad -->
            <div class="absolute top-6 right-6 w-16 h-16 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm">
              <svg class="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"></path>
              </svg>
            </div>
            
            <!-- Contenido -->
            <div class="relative z-10 text-white">
              <div class="flex items-center mb-4">
                <div class="w-2 h-8 bg-white rounded-full mr-4"></div>
                <h4 class="text-2xl font-bold">Protección de Datos Médicos</h4>
              </div>
              <p class="text-blue-100 leading-relaxed text-lg font-light">
                Infraestructura con cifrado de nivel hospitalario, cumplimiento de normativas internacionales de privacidad y protocolos de seguridad certificados.
              </p>
              
              <!-- Características -->
              <div class="mt-6 space-y-3">
                <div class="flex items-center text-blue-100">
                  <div class="w-2 h-2 bg-white rounded-full mr-3 flex-shrink-0"></div>
                  <span class="text-sm font-medium">Cifrado AES-256</span>
                </div>
                <div class="flex items-center text-blue-100">
                  <div class="w-2 h-2 bg-white rounded-full mr-3 flex-shrink-0"></div>
                  <span class="text-sm font-medium">Cumplimiento HIPAA</span>
                </div>
                <div class="flex items-center text-blue-100">
                  <div class="w-2 h-2 bg-white rounded-full mr-3 flex-shrink-0"></div>
                  <span class="text-sm font-medium">Certificación ISO 27001</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tiempo de Procesamiento -->
        <div class="group relative overflow-hidden rounded-xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1">
          <div class="relative bg-gradient-to-br from-green-900 via-green-800 to-green-700 p-8 min-h-80">
            <div class="absolute inset-0 bg-gradient-to-br from-green-600/20 to-transparent"></div>
            
            <!-- Icono de velocidad -->
            <div class="absolute top-6 right-6 w-16 h-16 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm">
              <svg class="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clip-rule="evenodd"></path>
              </svg>
            </div>
            
            <!-- Contenido -->
            <div class="relative z-10 text-white">
              <div class="flex items-center mb-4">
                <div class="w-2 h-8 bg-white rounded-full mr-4"></div>
                <h4 class="text-2xl font-bold">Tiempo de Procesamiento</h4>
              </div>
              <p class="text-green-100 leading-relaxed text-lg font-light">
                Análisis completo en 30-60 segundos. Sistema optimizado para alta demanda con disponibilidad 24/7 y soporte técnico especializado.
              </p>
              
              <!-- Características -->
              <div class="mt-6 space-y-3">
                <div class="flex items-center text-green-100">
                  <div class="w-2 h-2 bg-white rounded-full mr-3 flex-shrink-0"></div>
                  <span class="text-sm font-medium">Procesamiento en tiempo real</span>
                </div>
                <div class="flex items-center text-green-100">
                  <div class="w-2 h-2 bg-white rounded-full mr-3 flex-shrink-0"></div>
                  <span class="text-sm font-medium">Disponibilidad 24/7</span>
                </div>
                <div class="flex items-center text-green-100">
                  <div class="w-2 h-2 bg-white rounded-full mr-3 flex-shrink-0"></div>
                  <span class="text-sm font-medium">Soporte técnico especializado</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- CTA Principal -->
      <div class="text-center mt-12">
        <div class="bg-gradient-to-r from-blue-900 to-green-900 rounded-2xl p-8 shadow-2xl">
          <h4 class="text-2xl font-bold text-white mb-4">¿Listo para experimentar el futuro del análisis clínico?</h4>
          <p class="text-blue-100 mb-6 text-lg font-light">Únase a los profesionales de la salud que ya confían en SABIOLAB</p>
          <!-- <button 
            @click="openAuthModal('register')" 
            class="px-12 py-4 bg-white text-blue-900 rounded-xl hover:bg-gray-50 transition-all text-lg font-bold uppercase tracking-wider shadow-xl transform hover:scale-105"
          >
            Solicitar Demostración
          </button> -->
        </div>
      </div>
    </section>

    <!-- Auth Modal -->
    <AuthModal 
      :is-open="showAuthModal" 
      :initial-mode="authMode"
      @close="closeAuthModal"
      @success="handleAuthSuccess"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import AuthModal from '../components/AuthModal.vue';
import { useAuthStore } from '../stores/auth';

const authStore = useAuthStore();
const showAuthModal = ref(false);
const authMode = ref<'login' | 'register'>('login');

const openAuthModal = (mode: 'login' | 'register') => {
  authMode.value = mode;
  showAuthModal.value = true;
};

const closeAuthModal = () => {
  showAuthModal.value = false;
};

const handleAuthSuccess = () => {
  // El usuario se ha autenticado exitosamente
  // Redirigir a la vista de reportes
  window.location.href = '/reports';
};
</script>
